import { ReactNode, useEffect, useMemo } from 'react'
import { AtendimentoType, initialQueryState, OptionsHelper, useDebounce } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  IOption,
  TableFilter,
  TableFilters,
} from 'app/components'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Novo vínculo"). */
  actions?: ReactNode
}

export const TiposAtendimentoVinculosListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.empresa ? `empresa:${debouncedFilters.empresa.value}` : '',
        debouncedFilters.tipoAtendimento
          ? `tipoAtendimento:${debouncedFilters.tipoAtendimento.value}`
          : '',
        debouncedFilters.unidade ? `unidade.id:${debouncedFilters.unidade.value}` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Unidade depende da instituição: sem instituição, não há unidade.
  useEffect(() => {
    if (!filters.empresa && filters.unidade) setFilter('unidade', undefined)
  }, [filters.empresa, filters.unidade, setFilter])

  const empresaParams = useMemo(
    () => ({ empresaId: Number(filters.empresa?.value) }),
    [filters.empresa?.value]
  )

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.tipoAtendimento && {
      key: 'tipoAtendimento',
      label: 'Tipo',
      value: filters.tipoAtendimento.label,
      onRemove: () => setFilter('tipoAtendimento', undefined),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.unidade && {
      key: 'unidade',
      label: 'Unidade/Setor',
      value: filters.unidade.label,
      onRemove: () => setFilter('unidade', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'vínculo encontrado' : 'vínculos encontrados'}`
          }
        />
      }
    >
      <TableFilter label='Tipo de atendimento'>
        <CustomSelect
          placeholder='Selecione um tipo'
          onChange={(tipo?: IOption<AtendimentoType>) =>
            setFilter('tipoAtendimento', tipo ?? undefined)
          }
          fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
          value={filters.tipoAtendimento}
        />
      </TableFilter>
      <TableFilter label={t('empresa.label')}>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          value={filters.empresa}
        />
      </TableFilter>
      <TableFilter label='Unidade/Setor'>
        <CustomSelect
          placeholder={
            filters.empresa
              ? 'Selecione uma unidade'
              : `Selecione a ${t('empresa.label.lowercase')}`
          }
          onChange={(unidade?: IOption<number>) => setFilter('unidade', unidade ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsUnidades}
          fetchOptionsParams={empresaParams}
          value={filters.unidade}
          isDisabled={!filters.empresa}
        />
      </TableFilter>
    </TableFilters>
  )
}
